import React from 'react'
import { ContentColumn } from '../..'
import BenchmarkingPlaceholder from '../../components/BenchmarkingPlaceholder'
import { TimepointPrevalence } from './TimepointPrevalence'
import { CalculationCount } from '../CalculationCount/CalculationCount'
import { CalculationTimeInterval } from '../CalculationTimeInterval/CalculationTimeInterval'
import { CalculationTimepointCount } from '../CalculationTimepointCount/CalculationTimepointCount'
import { CalculationTaskPrevalence } from '../CalculationTaskPrevalence/CalculationTaskPrevalence'
import { CalculationSimTaskTime } from '../CalculationSimTaskTime/CalculationSimTaskTime'
import { CalculationSimTaskExtraTime } from '../CalculationSimTaskExtraTime/CalculationSimTaskExtraTime'
import { CalculationSimTaskTimePerBrukare } from '../CalculationSimTaskTimePerBrukare/CalculationSimTaskTimePerBrukare'
import { CalculationSimTaskExtraTimePerBrukare } from '../CalculationSimTaskExtraTimePerBrukare/CalculationSimTaskExtraTimePerBrukare'

// Översikt — kommunjämförande "big picture"-vyer. Sidordningen följer designsummeringen.
// Tidpunktsprevalens (sida 4) är den enda färdigbyggda vyn; övriga är stubbar som
// fylls på inkrementellt med riktiga grafer (MV + service + controller + hook per vy).

// *-noten ska visas i UI:t på de simulerade tid-vyerna (sida 7–10).
const simuleringsNot = (
  <>
    <b>* Om beräkningen av tid per insats</b>
    <br />
    Ramtidsberäkningen ger total tid per beräkning, inte per insats. Tiden per insats är därför en
    simulering baserad på nivå × frekvens × förekomst utan hänsyn till samordningsvinster — en
    modellerad fördelning, inte uppmätt tid. Syftet är att visa vilka insatser som driver de totala
    volymerna i respektive kommun.
  </>
)

export const benchmarkingCalculationTimepointsExtra: ContentColumn[] = [
  {
    pageTitle: 'Antal beräkningar',
    content: (
      <CalculationCount
        text={
          <>
            <b>Beskrivning</b>
            <br />
            Totalt antal beräkningar (senaste per brukare, icke-draft, icke-arkiverad) per kommun.
            <br />
            <br />
            <b>Tolkning</b>
            <br />
            En hög siffra kan spegla kommunstorlek, men också hur aktivt verktyget används. Jämför
            med kommunens befolkningsunderlag för att bedöma täckningsgraden.
          </>
        }
      />
    ),
  },
  {
    pageTitle: 'Andel beräkningar inom intervall av beräknad tid',
    content: (
      <CalculationTimeInterval
        text={
          <>
            <b>Beskrivning</b>
            <br />
            Fördelning av beräkningar (senaste per brukare) över intervall av total beräknad tid per
            månad. Varje stapelgrupp är ett tidsintervall, varje stapel inom gruppen en kommun.
            <br />
            <br />
            <b>Tolkning</b>
            <br />
            En tyngdpunkt i låga intervall kan signalera att verktyget används för brukare med
            begränsat vårdbehov, eller att beräkningarna är ofullständiga. En bred spridning är
            typisk för kommuner med varierat brukarbestånd.
          </>
        }
      />
    ),
  },
  {
    pageTitle: 'Antal tidpunkter per beräkning',
    content: (
      <CalculationTimepointCount
        text={
          <>
            <b>Beskrivning</b>
            <br />
            Distribution av antal tidpunkter per beräkning (senaste per brukare), per kommun.
            Varje stapel visar andelen beräkningar med ett givet antal tidpunkter (0 = enbart
            övriga insatser utan koppling till specifik tidpunkt).
            <br />
            <br />
            <b>Tolkning</b>
            <br />
            En hög andel vid låga antal tidpunkter kan indikera enkla beräkningar eller att
            verktyget används selektivt. Kommuner med bred insatsmix syns som spridning över
            flera kategorier.
          </>
        }
      />
    ),
  },
  {
    pageTitle: 'Tidpunktsprevalens',
    content: (
      <TimepointPrevalence
        text={
          <>
            <b>Beskrivning</b>
            <br />
            Andelen brukare per kommun vars senaste beräkning innehåller respektive tidpunkt
            (Morgon, Förmiddag, Lunch osv). "Övrig" avser insatser utan koppling till specifik tidpunkt.
            <br />
            <br />
            <b>Bakgrund</b>
            <br />
            Beräkningsstödet används av handläggare för att bedöma hemtjänstbehov per brukare.
            Tidpunktsmixen speglar hur kommunen fördelar insatser över dygnet.
            <br />
            <br />
            <b>Tolkning</b>
            <br />
            En hög andel på Morgon är vanlig och förväntat. Stora avvikelser mot andra kommuner
            kan signalera skillnader i hur insatser definieras eller hur beviljandeprocessen ser ut.
          </>
        }
      />
    ),
  },
  {
    pageTitle: 'Antal insatser per beräkning',
    content: (
      <BenchmarkingPlaceholder
        title="Antal insatser per beräkning"
        text={
          <>
            <b>Beskrivning</b>
            <br />
            Distribution av antal insatser per beräkning, per kommun. Exkluderar övriga insatser.
          </>
        }
      />
    ),
  },
  {
    pageTitle: 'Insatsprevalens',
    content: (
      <CalculationTaskPrevalence
        text={
          <>
            <b>Beskrivning</b>
            <br />
            Andel beräkningar (senaste per brukare) som innehåller respektive tidpunktsinsats,
            sorterat efter förekomst. Exkluderar övriga insatser. Visar enbart den egna kommunens
            data — insatsnamn varierar mellan kommuner.
            <br />
            <br />
            <b>Tolkning</b>
            <br />
            De vanligaste insatserna driver beräkningstid. En insats med hög prevalens som också
            har hög nivå eller frekvens är en viktig nyckel att titta närmre på.
          </>
        }
      />
    ),
  },
  {
    pageTitle: 'Andel av total tid per insats, tidpunktsinsatser',
    content: (
      <CalculationSimTaskTime
        text={
          <>
            <b>Beskrivning</b>
            <br />
            Andel (%) av total simulerad tid som varje tidpunktsinsats står för (Bereda måltider,
            Dusch, Förflyttning …) — endast den egna kommunens insatser. Svarar på: "hur är
            sammansättningen?"
            <br />
            <br />
            <b>Tolkning</b>
            <br />
            De insatser som tar störst andel av tiden är de som driver volymen. En insats med hög
            andel som dessutom har hög prevalens (sida 6) påverkar verksamheten brett.
            <br />
            <br />
            {simuleringsNot}
          </>
        }
      />
    ),
  },
  {
    pageTitle: 'Andel av total tid per insats, övriga insatser',
    content: (
      <CalculationSimTaskExtraTime
        text={
          <>
            <b>Beskrivning</b>
            <br />
            Andel (%) av total simulerad tid som varje övrig insats står för (Promenad, Tvätt,
            Städa bostaden …) — endast den egna kommunens insatser. Övriga insatser har inga
            samordningsvinster, vilket gör simuleringen mer exakt än för tidpunktsinsatser.
            <br />
            <br />
            {simuleringsNot}
          </>
        }
      />
    ),
  },
  {
    pageTitle: 'Antal timmar per insats och brukare, tidpunktsinsatser',
    content: (
      <CalculationSimTaskTimePerBrukare
        text={
          <>
            <b>Beskrivning</b>
            <br />
            Simulerad tid per insats ÷ antal brukare som har insatsen (h/brukare och månad) för
            tidpunktsinsatser — endast den egna kommunens insatser. Svarar på: "hur intensiv är
            vården för dem som faktiskt får insatsen?"
            <br />
            <br />
            <b>Tolkning</b>
            <br />
            En insats med hög h/brukare drar stor tid per individ. Insatser med både hög prevalens
            (sida 6) och hög h/brukare är de tyngsta volymdrivarna.
            <br />
            <br />
            {simuleringsNot}
          </>
        }
      />
    ),
  },
  {
    pageTitle: 'Antal timmar per insats och brukare, övriga insatser',
    content: (
      <CalculationSimTaskExtraTimePerBrukare
        text={
          <>
            <b>Beskrivning</b>
            <br />
            Simulerad tid per insats ÷ antal brukare som har insatsen (h/brukare och månad) för
            övriga insatser — endast den egna kommunens insatser. Övriga insatser har inga
            samordningsvinster, vilket gör simuleringen mer exakt.
            <br />
            <br />
            {simuleringsNot}
          </>
        }
      />
    ),
  },
]
